import { z } from 'zod';

/**
 * Menções do composer (`@viajante`, `@local`, `@data`, `@viagem`) — o dado
 * ESTRUTURADO que um token inline representa no texto. Viaja ao lado do `text`
 * pela MESMA régua de `confirmation`/`selection`/`domainHint`: a escolha foi do
 * usuário num menu determinístico, não uma leitura que o LLM faz da prosa.
 *
 * Cada variante carrega o `text` literal que o token ocupa no `text` enviado
 * (o rótulo humano, ex.: "Marina Souza", "GRU", "15–17 Out") — é como o
 * consumidor liga a menção à posição na frase sem precisar de offsets, que
 * quebrariam a cada normalização do texto.
 *
 * O cliente é a fonte destes ids e por isso eles têm a MESMA confiança do
 * `selection.stay.travelers`: autorização (o viajante está no roster? a viagem é
 * do usuário?) continua no backend; aqui é só shape.
 */

const MENTION_TEXT_MAX = 200;
const MentionTextSchema = z.string().min(1).max(MENTION_TEXT_MAX);

/** Mesmo shape de `selection.stay.travelers` (e do `TravelerRef` do genui-kit). */
const MentionTravelerRefSchema = z.object({ id: z.string().min(1), name: z.string() }).strict();

/**
 * Ref de lugar no formato `<prefixo>:<id nativo>` do voll-places. Espelha os
 * prefixos de `parseLocationRef` (`REF_PREFIXES` em
 * `packages/voll-apollo/.../location_types.ts`) — o domain não pode depender do
 * voll-apollo (inverteria a dependência), então a lista vive aqui por regex.
 * NÃO existe `city:`: cidade-metro chega como `airport:SAO`.
 */
export const MENTION_PLACE_REF_PATTERN = /^(google|airport|bus):.+$/;

export const MentionSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('traveler'),
      text: MentionTextSchema,
      traveler: MentionTravelerRefSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('place'),
      text: MentionTextSchema,
      ref: z.string().max(500).regex(MENTION_PLACE_REF_PATTERN),
      /** Rótulo legível do lugar (título do lugar, nome do aeroporto). */
      label: z.string().min(1).max(MENTION_TEXT_MAX),
    })
    .strict(),
  z
    .object({
      kind: z.literal('date'),
      text: MentionTextSchema,
      date: z.iso.date(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('date_range'),
      text: MentionTextSchema,
      from: z.iso.date(),
      to: z.iso.date(),
    })
    .strict()
    // Um dia só é `date`; o intervalo precisa de dois dias distintos, na ordem
    // (mesma regra de `stay.checkout > checkin`).
    .refine(({ from, to }) => to > from, { message: 'to must be after from', path: ['to'] }),
  z
    .object({
      kind: z.literal('trip'),
      text: MentionTextSchema,
      // Mesmo limite do `tripId` top-level do request.
      tripId: z.string().min(1).max(100),
      /** Rótulo da viagem como a sidebar mostra (nome manual ou rota · datas). */
      label: z.string().min(1).max(MENTION_TEXT_MAX),
    })
    .strict(),
]);
export type Mention = z.infer<typeof MentionSchema>;
export type MentionKind = Mention['kind'];

/** Teto de menções por mensagem — cabe um roster de viagem inteiro (9 hóspedes,
 *  como `stay.travelers`) mais lugares, datas e viagem, sem virar vetor de
 *  payload arbitrário. */
export const MAX_MENTIONS = 20;
export const MentionListSchema = z.array(MentionSchema).max(MAX_MENTIONS);
